import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef, type ReactNode } from 'react';
import { api, API_BASE } from '@/lib/api';
import { useQueryClient } from '@tanstack/react-query';
import { normalizeRoles, hasRole as hasNormalizedRole, hasAnyRole as hasAnyNormalizedRole } from '@/lib/permissions';
import type { UserWithRoles } from '@/lib/types';
import { refreshBadge, clearBadge, setSessionAuthenticated } from '@/lib/push-client';

interface AuthContextType {
  user: UserWithRoles | null;
  loading: boolean;
  login: (organizationCode: string, uniqueId: string, password: string) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (role: string) => boolean;
  hasAnyRole: (roles: string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserWithRoles | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();
  const userRef = useRef<UserWithRoles | null>(null);

  const refreshUser = useCallback(async () => {
    try {
      const token = sessionStorage.getItem("access_token");
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;
      const response = await fetch(`${API_BASE}/auth/me`, { headers, credentials: 'include' });
      if (response.status === 401) {
        sessionStorage.removeItem("access_token");
        setUser(null);
        setSessionAuthenticated(false);
        clearBadge();
        return;
      }
      if (!response.ok) { setUser(null); setSessionAuthenticated(false); clearBadge(); return; }
      const body = await response.json();
      if (body.success) {
        const next = body.user as UserWithRoles;
        const prev = userRef.current;
        const changed = next?.id !== prev?.id
          || (next?.roles?.join('|') ?? '') !== (prev?.roles?.join('|') ?? '')
          || (next?.permissions?.join('|') ?? '') !== (prev?.permissions?.join('|') ?? '');
        userRef.current = next ?? null;
        if (changed) queryClient.clear();
        setUser(next ?? null);
        setSessionAuthenticated(true);
        void refreshBadge();
      }
      else { setUser(null); setSessionAuthenticated(false); }
    } catch {
      setUser(null);
    }
  }, [queryClient]);

  const bootstrapRef = useRef(false);

  useEffect(() => {
    if (bootstrapRef.current) return;
    bootstrapRef.current = true;

    // Always validate the session against /auth/me on every boot. The Worker
    // issues a persistent httpOnly session_token cookie and prefers it over the
    // Bearer header, so /auth/me succeeds from that cookie even after the PWA or
    // tab is closed (which clears sessionStorage). Gating this probe on a stored
    // sessionStorage token caused returning users with a valid cookie to be sent
    // to the login page on restart. Unauthenticated visitors simply get a 401/404
    // from /auth/me and remain logged out.
    refreshUser().finally(() => setLoading(false));
  }, [refreshUser]);

  const login = useCallback(async (organizationCode: string, uniqueId: string, password: string) => {
    const res = await api.post<{ success: boolean; accessToken: string; user: UserWithRoles }>('/auth/login', { organizationCode, uniqueId, password });
    if (res.success) {
      if (res.accessToken) {
        sessionStorage.setItem("access_token", res.accessToken);
      }
      userRef.current = res.user;
      setUser(res.user);
      setSessionAuthenticated(true);
      queryClient.clear();
      void refreshBadge();
    }
  }, [queryClient]);

  const logout = useCallback(() => {
    api.post('/auth/logout').finally(() => {
      sessionStorage.removeItem("access_token");
      userRef.current = null;
      setUser(null);
      setSessionAuthenticated(false);
      queryClient.clear();
      clearBadge();
      window.location.href = '/login';
    });
  }, [queryClient]);

  const normalizedRoles = useMemo(() => (user ? normalizeRoles(user.roles) : []), [user]);

  const hasRole = useCallback((role: string) =>
    hasNormalizedRole(normalizedRoles, role), [normalizedRoles]);

  const hasAnyRole = useCallback((roles: string[]) =>
    hasAnyNormalizedRole(normalizedRoles, roles), [normalizedRoles]);

  const hasPermission = useCallback((permission: string) =>
    !!user?.permissions?.includes(permission), [user?.permissions]);

  const hasAnyPermission = useCallback((permissionKeys: string[]) =>
    permissionKeys.some((key) => !!user?.permissions?.includes(key)), [user?.permissions]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refreshUser, hasRole, hasAnyRole, hasPermission, hasAnyPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
